import { agGridIconsConfig } from '@/explore/ffl-detail/utils.mjs';
import { BlacklistDealerListInterface } from '@/model/blacklistDealerList.mjs';
import { Box, Button, Heading, useDisclosure } from '@chakra-ui/react';
import { ColDef, GridOptions, GridReadyEvent, IGetRowsParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import BlacklistDealersDataSource from '../ducks/blacklistDealersDataSource';
import { SERVER_PAGE_SIZE } from '@/model/Constants';
import { updateBlacklistedStatus } from '@/api/apis';
import '../index.scss';
import ModalComponent from '@/common/Modal';
import AddBlacklist from '../elements/AddBlacklist';
import Licensee from '@/model/licensee.mjs';
import ConfirmationModal from '@/common/ConfirmationModal';
import * as constant from '@/utils/constants'
import { logAnalyticsEvent } from '@/utils/analyticsLogger';
import { analyticsEvents } from '@/utils/analyticsEvents';
import { getEightDigitLicenseKey } from '@/queue/utils';

interface BlacklistDealersProps {
    doRefresh: number,
    isOpen: boolean,
    onClose: () => void,
}

interface mailAddressType {
        city: string | null,
        state: string | null,
        street: string | null
        zipCode: string | null,
}

function BlacklistDealers({ doRefresh, isOpen, onClose }: BlacklistDealersProps) {
    const blacklistDealersDataSource = new BlacklistDealersDataSource();
    const gridRef = useRef<AgGridReact<BlacklistDealerListInterface>>(null);
    const gridStyle = useMemo(() => ({ width: '100%' }), []);
    const [isRemove, setIsRemove] = useState(false);
    const [licenseKeyId, setLicenseKeyId] = useState<string | null>("");
    const [isAddBlacklistLoading, setIsAddBlacklistLoading] = useState(false);
    const [licensee, setLicensee] = useState<Licensee | undefined>()
    const { isOpen: isOpenRemoveModal, onClose: onCloseRemoveModal, onOpen: onOpenRemoveModal } = useDisclosure()

    useEffect(() => {
        if (doRefresh === 3) reFetchData();
    }, [doRefresh])

    const mailAddressConcat = ({ value }: { value: mailAddressType }) => {
        return <>{value && value.street && `${value?.street}, ${value?.city}, ${value?.state}, ${value?.zipCode}`}</>

    }
    const businessNameRender = ({ value }: any) => {
        return <Box textTransform={'uppercase'} className="transfer-status-wrapper">
            {!value ? <><img src="https://www.ag-grid.com/example-assets/loading.gif" />&nbsp; Loading</> : value}
        </Box>
    }
    const licenseKeyRenderer = ({value}:any) => {
        return <Box>
        {!value ? <></> : value[0]}
    </Box>
    }

    const handleRemove = async () => {
        if (licenseKeyId) {
            logAnalyticsEvent(analyticsEvents.dealerRemovedFromBlacklist);
            setIsRemove(true);
            try {
                const licenseKey = licenseKeyId
                await updateBlacklistedStatus({ data: { isBlacklisted: false },licenseKey})
                setTimeout(()=>{
                    reFetchData();
                },2000)
                setIsRemove(false);
                onCloseRemoveModal();
                setLicenseKeyId("");
            } catch (error) {
                console.log({ error });
                setIsRemove(false);
                onCloseRemoveModal();
                setLicenseKeyId("");
            }
        }
    }
    const actionBlacklistDealers = ({ data }: { data: BlacklistDealerListInterface }) => {
        const handleRemoveAction = () => {
            const licenseKey = getEightDigitLicenseKey(data?.fullLicenseKeys[0])
            setLicenseKeyId(licenseKey)
            onOpenRemoveModal();
        }

        return <>
            {data?.fullLicenseKeys?.length!=0 && <button disabled={isRemove} className="remove-blacklist-dealers-btn" onClick={handleRemoveAction}>Remove</button>}

        </>
    }
    const onGridReady = useCallback((gridEvent: GridReadyEvent<BlacklistDealerListInterface, any>) => {
        const dataSource = {
            rowCount: undefined,
            getRows(params: IGetRowsParams): void {
                const pageNumber = params.startRow / SERVER_PAGE_SIZE + 1;
                blacklistDealersDataSource.fetchData({ ...params, startRow: pageNumber })
                    .then((result) => {
                        const lastRow = result.metadata.hasMorePages ? Number(gridEvent.api.getDisplayedRowCount()) + SERVER_PAGE_SIZE : gridEvent.api.getDisplayedRowCount() - 1 + result.data.length;
                        if (result.data.length === 0 && gridEvent.api.getDisplayedRowCount() <= 1) {
                            onBtShowNoRows();
                        } else {
                            onBtHide();
                        }
                        params.successCallback(result.data, lastRow);
                    })
                    .catch((error) => {
                        console.error('Error while fetching rows for ag-Grid', error);
                        params.failCallback();
                    });
            }
        };
        gridEvent.api.setGridOption('datasource', dataSource);
    }, []);
    const columnDefs: ColDef[] = [
        {
            field: 'primaryBusinessName',
            headerName: constant.BUSINESS_NAME,
            width: 400,
            cellRenderer: businessNameRender
        },
        {
            field: 'fullLicenseKeys',
            headerName: 'License Number',
            width: 300,
            cellRenderer: licenseKeyRenderer
        },
        {
            field: 'address',
            headerName: 'Mailing Address',
            width: 450,
            cellRenderer: mailAddressConcat
        },
        {
            field: 'primaryBusinessType',
            headerName: 'Business Type',
            width: 250,
        },
        {
            headerName: 'Action',
            width: 250,
            cellRenderer: actionBlacklistDealers
        },
    ];

    const defaultColDef = useMemo(
        () => ({
            resizable: true,
            sortable: true
        }),
        []
    );

    const gridOptions: GridOptions<BlacklistDealerListInterface> = {
        rowModelType: 'infinite',
        infiniteInitialRowCount: 1,
        // autoSizeStrategy: {
        //     type: 'fitCellContents'
        // },
        maxConcurrentDatasourceRequests: 2,
        cacheBlockSize: SERVER_PAGE_SIZE,
        cacheOverflowSize: 2,


        // column definitions
        columnDefs,
        defaultColDef,

        animateRows: true,
        suppressRowClickSelection: true,

        // cell detail options
        suppressCellFocus: true,

        onGridReady,
        // misc options
        debug: false,
        icons: agGridIconsConfig,
        overlayNoRowsTemplate: '<div class="not-found-box"><img src="/images/ui/not-found-icon.png"></img><p class="not-found-box__title">No Blacklisted Dealers found</p><p class="not-found-box__message">To add blacklist dealers click on the ‘add to blacklist’ button on the top right corner</p></div>'
    };
    function reFetchData() {
        gridRef.current?.api.setRowCount(0);
        gridRef.current?.api?.refreshInfiniteCache();
        onBtHide();
    }

    const onBtShowNoRows = useCallback(() => {
        gridRef.current?.api.showNoRowsOverlay();
    }, []);

    function onBtHide() {
        gridRef.current?.api.hideOverlay();
    }
    const onAddBlacklistHandler = async () => {
        if (licensee?.id) {
            logAnalyticsEvent(analyticsEvents.dealerAddedToBlacklist);
            setIsAddBlacklistLoading(true);
            try {
                const licenseKey = licensee?.id?.replace(/[-X]/g, '')
                await updateBlacklistedStatus({ data: { isBlacklisted: true }, licenseKey })
                setIsAddBlacklistLoading(false);
                onClose();
                setTimeout(() => {
                    reFetchData();
                },2000)
                setLicensee(undefined);
            } catch (error) {
                console.log({ error });
                onClose();
                setLicensee(undefined);
                setIsAddBlacklistLoading(false);
            }
        }
    }
    const handleClose = () => {
        setLicensee(undefined);
        onClose();
    }

    
    return (
        <>
            <div style={gridStyle} className='blacklist-grid'>
                <AgGridReact
                    className={'ag-theme-alpine'}
                    ref={gridRef}
                    gridOptions={gridOptions}
                />
            </div>
            {isOpenRemoveModal && <ConfirmationModal
                isOpen={isOpenRemoveModal}
                modalHeader={'Please confirm'}
                onClose={onCloseRemoveModal}
                isCancelBtnDisable={false}
                isConfirmBtnDisable={isRemove}
                isConfirmBtnLoading={isRemove}
                onClickConfirmBtn={handleRemove}
                modalContent={
                    <>{constant.REMOVE_DEALER_FROM_BLACKLIST_CONFIRMATION} </>
                }
            />}
            <ModalComponent
                isOpen={isOpen}
                onClose={handleClose}
                title={<>Add Dealer to Blacklist</>}
                variant={2}
                className="add-blacklist-modal"
                footer={
                    licensee?.id ?
                        <div>
                            <Button
                                colorScheme="blue"
                                onClick={handleClose}
                                background="#ffffff"
                                color="#0C1E46"
                                border="1px solid #0C1E46"
                                padding="8px 11px"
                                marginRight="20px"
                            >
                                Cancel
                            </Button>
                            <Button
                                colorScheme="blue"
                                background="#0C1E46"
                                padding="8px 20px"
                                onClick={onAddBlacklistHandler}
                                isLoading={isAddBlacklistLoading}
                                isDisabled={isAddBlacklistLoading || licensee?.isBlacklisted}
                            >
                                Add
                            </Button>
                        </div> : <div></div>
                }
            >
                <AddBlacklist licensee={licensee} setLicensee={setLicensee} />
            </ModalComponent>
        </>
    )
}

export default BlacklistDealers
